import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";

/** Milliseconds before token expiry at which to trigger proactive refresh */
const REFRESH_BEFORE_EXPIRY_MS = 60_000; // 1 minute

/** Retry delays for transient proactive-refresh failures (exponential backoff) */
const RETRY_DELAYS_MS = [30_000, 120_000, 300_000]; // 30 s, 2 min, 5 min

/**
 * Decode JWT exp claim without verifying signature (client-side only).
 * Returns expiry in epoch milliseconds, or null if the token can't be decoded.
 */
function getTokenExpiryMs(token: string): number | null {
  try {
    const b64url = token.split(".")[1];
    if (!b64url) return null;
    // Convert base64url → standard base64 (RFC 4648 §5) before decoding.
    // JWTs use '-' and '_' instead of '+' and '/', and omit '=' padding.
    const b64 = b64url
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(b64url.length + (4 - (b64url.length % 4)) % 4, "=");
    const payload = JSON.parse(atob(b64));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Proactively keeps the Auth0 access token fresh throughout the user session.
 *
 * ## Why this hook exists
 * Auth0 JS SDK caches tokens in `localStorage` (`cacheLocation: "localstorage"`).
 * The cache returns a token silently as long as it has not expired, BUT it only
 * refreshes it automatically when an outgoing API call needs one. Between API
 * calls the token can quietly expire, causing the next call to fail with a 401
 * before the reactive retry path even activates.
 *
 * This hook eliminates that window by scheduling a proactive silent refresh
 * 60 s before the JWT's `exp` claim is reached.
 *
 * ## Where it lives
 * Rendered inside `Auth0TokenInitializer` in `App.tsx`, which is mounted inside
 * `Auth0Provider`. This guarantees the hook always has access to the Auth0
 * context while the user is logged in.
 *
 * ## Refresh strategy
 * 1. On mount (when `isAuthenticated === true`), call `getAccessTokenSilently()`
 *    **without** `cacheMode: 'off'` — this reads from the SDK cache and avoids
 *    an unnecessary network round-trip on first load.
 * 2. Decode the JWT `exp` claim client-side (no signature verification needed —
 *    we only need the timestamp). Compute `delay = exp - now - 60 s`.
 * 3. After `delay` ms, call `getAccessTokenSilently({ cacheMode: 'off' })` to
 *    force a token exchange using the stored refresh token, bypassing the cache.
 * 4. Write the new token to `localStorage["authToken"]`:
 *    - `apiConfig.ts` reads this key as a synchronous fallback in environments
 *      where the async Auth0 getter is not yet registered.
 *    - The pro-portal pilet reads it directly for its own API calls.
 *    - The Guard View standalone window monitors this key via `storage` events to
 *      detect cross-tab logout (see `GuardViewStandalonePage.tsx`).
 * 5. Reschedule the next refresh for the newly issued token's expiry cycle.
 *
 * ## Fallback when `exp` cannot be decoded
 * If the JWT payload cannot be parsed (malformed token, future format change),
 * the hook retries after a fixed 25-minute window — conservative enough to
 * pre-empt a typical 30-minute Auth0 access token TTL.
 *
 * ## Failure handling
 * If the proactive refresh throws (e.g. refresh token revoked, network offline),
 * the error is logged but intentionally not re-thrown. The reactive 401-retry
 * interceptors in `apiConfig.ts` and `apiService.ts` will attempt a force-refresh
 * on the next failing API call, and will trigger logout if that too fails.
 *
 * ## Cleanup
 * The `useEffect` cleanup cancels any pending timeout and sets a `cancelled` flag
 * so in-flight `getAccessTokenSilently` promises do not update state after unmount
 * (e.g. during logout or hot-module replacement in development).
 */
export function useTokenRefresh() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    let cancelled = false;

    const scheduleNextRefresh = async (forceRefresh = false, retryCount = 0) => {
      try {
        const token = await getAccessTokenSilently(
          forceRefresh ? { cacheMode: "off" } : undefined
        );

        if (cancelled) return;

        // Keep localStorage fallback fresh for apiConfig / pro-portal pilet
        localStorage.setItem("authToken", token);

        const expiresAt = getTokenExpiryMs(token);
        if (expiresAt === null) {
          // Can't decode expiry — retry in 25 minutes as a safe default
          timeoutRef.current = setTimeout(() => {
            if (!cancelled) scheduleNextRefresh(true);
          }, 25 * 60_000);
          return;
        }

        const delay = Math.max(
          expiresAt - Date.now() - REFRESH_BEFORE_EXPIRY_MS,
          0
        );

        timeoutRef.current = setTimeout(() => {
          if (!cancelled) scheduleNextRefresh(true);
        }, delay);
      } catch (err) {
        console.warn("[TokenRefresh] Proactive refresh failed:", err);
        // Schedule a retry with exponential backoff so a transient network
        // hiccup does not permanently stop proactive refresh for the session.
        // Once retries are exhausted, the reactive 401-retry interceptors in
        // apiConfig / apiService take over on the next failing API call.
        if (retryCount < RETRY_DELAYS_MS.length) {
          const delay = RETRY_DELAYS_MS[retryCount];
          timeoutRef.current = setTimeout(() => {
            if (!cancelled) scheduleNextRefresh(true, retryCount + 1);
          }, delay);
        }
      }
    };

    scheduleNextRefresh();

    return () => {
      cancelled = true;
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isAuthenticated, getAccessTokenSilently]);
}
